import { useRef, useState, useEffect, useCallback } from 'react';
import useFetchData from '../../hooks/useFetchData';
import LoadingSpinner from '../Common/LoadingSpinner/LoadingSpinner';
import SkeletonCard from '../Common/SkeletonCard/SkeletonCard';
import ModalOverlay from '../Common/ModalOverlay/ModalOverlay';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import PortalModal from '../Common/Modal/PortalModal';
import ProductDetails from './ProductDetails';
import { useCart } from '../../context/CartContext';
import Cart from './Cart';
import { InformationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import AddressForm from '../Orders/New/AddressForm';
import { Form, FormikProvider, useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import addressService from '../../services/addressService';
import { createAddressValidationSchema } from '../../utils/addressValidation';

import SuccessMessage from './SuccessMessage';

const TABS = [
    { id: 'capsule_refill', label: 'Rechargement de capsule' },
    { id: 'other', label: 'Autres produits' }
];

// Le schéma de validation est maintenant importé depuis utils/addressValidation.js

const Products = () => {
    const { data: products, error, isLoading } = useFetchData('/products');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [activeTab, setActiveTab] = useState('capsule_refill');
    const modalRef = useRef(null);
    const { addToCart, cart, resetCart, paymentUrl, isSubmitting, orderProducts, refillCapsules, user } = useCart();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [useSameAddress, setUseSameAddress] = useState(true);
    const [isCreatingAddresses, setIsCreatingAddresses] = useState(false);
    const navigate = useNavigate();

    // Fonction centralisée pour les valeurs initiales du formulaire
    const getInitialFormValues = useCallback(() => ({
        shippingAddressId: user?.addresses?.length > 0 ? user.addresses[0].id : null,
        shipping: {
            address_1: user?.addresses?.[0]?.street || "",
            city: user?.addresses?.[0]?.city || "",
            postcode: user?.addresses?.[0]?.postcode || "",
            country: user?.addresses?.[0]?.isoCountry || "",
            state: user?.addresses?.[0]?.state || "",
            first_name: user?.firstname || "",
            last_name: user?.lastname || "",
            company: "", // Champ company pour shipping (utilisé quand useSameAddress = true)
        },
        billingAddressId: user?.addresses?.length > 0 ? user.addresses[0].id : null,
        billing: {
            address_1: user?.addresses?.[0]?.street || "",
            city: user?.addresses?.[0]?.city || "",
            postcode: user?.addresses?.[0]?.postcode || "",
            country: user?.addresses?.[0]?.isoCountry || "",
            state: user?.addresses?.[0]?.state || "",
            first_name: user?.firstname || "",
            last_name: user?.lastname || "",
            company: user?.addresses?.[0]?.company || "",
        }
    }), [user]);

    useEffect(() => {
        if (paymentUrl) {
            // Réinitialiser tous les états pour permettre une nouvelle commande
            // Note: isCreatingAddresses devrait déjà être à false après la soumission réussie
            setShowAddressModal(false);
            setShowConfirmation(false);
            setSelectedProduct(null);

            // Réinitialiser le formulaire avec les valeurs par défaut de l'utilisateur
            formik.resetForm({
                values: getInitialFormValues()
            });
            setUseSameAddress(true);

            // Afficher le modal de succès
            setShowSuccessModal(true);
            modalRef.current?.showModal();
        }
    }, [paymentUrl, getInitialFormValues]);



    const handleShowDetails = (product) => {
        setSelectedProduct(product);
        modalRef.current?.showModal();
    };

    const handleAddToCart = (e, product) => {
        e.stopPropagation();
        addToCart(product);
    };

    const handleTabChange = (tabId) => {
        if (cart.length > 0) {
            setShowConfirmation(true);
            setSelectedProduct(null);
            modalRef.current?.showModal();

        } else {
            setActiveTab(tabId);
        }
    };

    const confirmTabChange = (confirm) => {
        if (confirm) {
            setActiveTab(activeTab === 'capsule_refill' ? 'other' : 'capsule_refill');
            resetCart();
        }
        setShowConfirmation(false);
        setSelectedProduct(null);
        modalRef.current?.hideModal();
    };

    const handlePayment = () => {
        if (paymentUrl) {
            window.open(paymentUrl, '_blank');
        }
    };

    const handleOrder = () => {
        // Toujours ouvrir le modal d'adresse, peu importe le type de commande
        setShowAddressModal(true);
        modalRef.current?.showModal();
    }


    const formik = useFormik({
        initialValues: getInitialFormValues(),
        validate: (values) => {
            try {
                // Utiliser le schéma dynamique basé sur useSameAddress
                const schema = createAddressValidationSchema(useSameAddress);
                schema.validateSync(values, { abortEarly: false });
                return {};
            } catch (error) {
                const errors = {};
                error.inner.forEach((err) => {
                    if (err.path) {
                        // Convertir le chemin Yup en structure d'objet
                        const pathParts = err.path.split('.');
                        let current = errors;
                        for (let i = 0; i < pathParts.length - 1; i++) {
                            if (!current[pathParts[i]]) {
                                current[pathParts[i]] = {};
                            }
                            current = current[pathParts[i]];
                        }
                        current[pathParts[pathParts.length - 1]] = err.message;
                    }
                });
                return errors;
            }
        },
        onSubmit: async (values) => {
            console.log('Form submitted with values:', values);
            console.log('useSameAddress:', useSameAddress);
            try {

                // S'assurer que les noms sont remplis
                values.shipping.first_name = user.firstname;
                values.shipping.last_name = user.lastname;
                values.shipping.email = user.email.toLowerCase().trim();

                // Créer l'adresse de livraison si nécessaire
                const shippingAddressId = await handleAddressCreation(values.shipping, 'shipping');

                // Gérer l'adresse de facturation
                let billingAddressId;
                if (useSameAddress) {
                    // Cas 1: Même adresse pour shipping et billing
                    // - Si shipping était une nouvelle adresse → une seule adresse créée, utilisée pour les deux
                    // - Si shipping était une adresse existante → même ID utilisé pour billing
                    billingAddressId = shippingAddressId;

                    // Synchroniser les données billing avec shipping (en priorisant billing.company)
                    const billingCompany = values.billing.company; // Préserver le champ company de billing
                    const shippingCompany = values.shipping.company; // Company depuis shipping
                    
                    values.billing = {
                        ...values.shipping,
                        first_name: values.shipping.first_name || user.firstname,
                        last_name: values.shipping.last_name || user.lastname,
                        email: user.email.toLowerCase().trim(),
                        // Prioriser billing.company, sinon utiliser shipping.company
                        company: billingCompany || shippingCompany || ""
                    };
                    values.billingAddressId = shippingAddressId;
                } else {
                    // Cas 2: Adresses différentes pour shipping et billing
                    // S'assurer que les champs obligatoires sont remplis
                    values.billing.first_name = values.billing.first_name || user.firstname;
                    values.billing.last_name = values.billing.last_name || user.lastname;
                    values.billing.email = user.email.toLowerCase().trim();

                    // Créer ou mettre à jour l'adresse de facturation séparément
                    billingAddressId = await handleAddressCreation(values.billing, 'billing');
                }

                const payload = {
                    shippingAddressId,
                    billingAddressId,
                    addresses: {
                        shipping: values.shipping,
                        billing: values.billing
                    }
                };

                console.log('Final payload:', payload);

                // Appeler la fonction appropriée selon le type
                if (activeTab === 'capsule_refill') {
                    await refillCapsules(payload);
                } else {
                    await orderProducts(payload);
                }

                setShowAddressModal(false);
            } catch (error) {
                console.error('Erreur lors de la création de la commande:', error);
                // Ne pas fermer le modal en cas d'erreur
                // isCreatingAddresses est géré automatiquement par handleAddressCreation
            }
        }

    })
    console.log(formik.errors)

    // Fonction pour gérer la création d'adresses
    // Fonction utilitaire pour comparer les données d'adresse
    const hasAddressChanged = useCallback((originalAddress, newAddressData) => {
        if (!originalAddress) return true;

        const fieldsToCompare = [
            { original: 'street', new: 'address_1' },
            { original: 'city', new: 'city' },
            { original: 'postcode', new: 'postcode' },
            { original: 'state', new: 'state' },
            { original: 'isoCountry', new: 'country' },
            { original: 'company', new: 'company' }
        ];

        return fieldsToCompare.some(field => {
            const originalValue = originalAddress[field.original] || '';
            const newValue = newAddressData[field.new] || '';
            return originalValue !== newValue;
        });
    }, []);

    const handleAddressCreation = useCallback(async (addressData, type) => {
        const addressId = formik.values[`${type}AddressId`];

        try {
            setIsCreatingAddresses(true);

            // Si une adresse est sélectionnée, vérifier s'il y a des changements
            if (addressId) {
                const originalAddress = user?.addresses?.find(addr => addr.id === parseInt(addressId));

                if (originalAddress && hasAddressChanged(originalAddress, addressData)) {
                    // Mettre à jour l'adresse existante
                    console.log('Mise à jour de l\'adresse existante:', addressId);
                    const result = await addressService.updateAddress(addressId, addressData);

                    if (!result.success) {
                        toast.error(result.error);
                        throw new Error(result.error);
                    }

                    toast.success('Adresse mise à jour avec succès');

                    // TODO: Optionnel - Rafraîchir les données utilisateur pour synchroniser l'interface
                    // await refreshUserData();

                    return addressId;
                } else {
                    // Aucun changement détecté, retourner l'ID existant
                    console.log('Aucun changement détecté pour l\'adresse:', addressId);
                    return addressId;
                }
            } else {
                // Créer une nouvelle adresse
                console.log('Création d\'une nouvelle adresse');
                const result = await addressService.createAddress(addressData);

                if (!result.success) {
                    toast.error(result.error);
                    throw new Error(result.error);
                }

                toast.success('Nouvelle adresse créée avec succès');

                // TODO: Optionnel - Rafraîchir les données utilisateur pour ajouter la nouvelle adresse
                // await refreshUserData();

                return result.address.id;
            }
        } finally {
            setIsCreatingAddresses(false);
        }
    }, [formik.values, user?.addresses, hasAddressChanged]);

    const updateFormAddress = (type, addressId) => {
        const address = user?.addresses?.find(address => address.id === parseInt(addressId))
        const firstName = user.firstname
        const lastName = user.lastname
        if (!address) {
            console.log('address not found')
            return
        }

        console.log(address)
        console.log(user)
        formik.setFieldValue(`${type}.address_1`, address.street)
        formik.setFieldValue(`${type}.city`, address.city)
        formik.setFieldValue(`${type}.postcode`, address.postcode)
        formik.setFieldValue(`${type}.state`, address.state)
        formik.setFieldValue(`${type}.country`, address.isoCountry)
        formik.setFieldValue(`${type}.first_name`, firstName)
        formik.setFieldValue(`${type}.last_name`, lastName)

        // Pré-remplir le champ company pour les adresses de facturation ET shipping
        formik.setFieldValue(`${type}.company`, address.company || "")
    }

    const handleUseSameAddress = () => {
        const newUseSameAddress = !useSameAddress;
        setUseSameAddress(newUseSameAddress);

        // Déclencher une revalidation avec le nouveau schéma
        setTimeout(() => {
            formik.validateForm();
        }, 0);

        // La synchronisation sera gérée par le useEffect
    }

    const handleGoToOrders = () => {
        navigate('/mon-compte/commandes')
    }

    // Référence pour éviter les boucles infinies
    const previousUseSameAddress = useRef(useSameAddress);
    const previousShippingAddressId = useRef(formik.values.shippingAddressId);

    // Déclencher une revalidation quand useSameAddress change
    useEffect(() => {
        formik.validateForm();
    }, [useSameAddress]);

    useEffect(() => {
        // Seulement synchroniser quand useSameAddress change ou quand une nouvelle adresse shipping est sélectionnée
        const useSameAddressChanged = previousUseSameAddress.current !== useSameAddress;
        const shippingAddressChanged = previousShippingAddressId.current !== formik.values.shippingAddressId;

        if (useSameAddress && user && (useSameAddressChanged || shippingAddressChanged)) {
            // Copier l'ID d'adresse de shipping vers billing
            formik.setFieldValue("billingAddressId", formik.values.shippingAddressId);

            // Copier les valeurs de shipping vers billing
            const shippingValues = { ...formik.values.shipping };
            const currentBillingCompany = formik.values.billing.company;

            // S'assurer que les champs obligatoires sont remplis même si shipping est vide
            const billingValues = {
                ...shippingValues,
                first_name: shippingValues.first_name || user.firstname || "",
                last_name: shippingValues.last_name || user.lastname || "",
                email: user.email?.toLowerCase().trim() || "",
                // Si on utilise une nouvelle adresse shipping (shippingAddressId = null), 
                // prioriser shipping.company, sinon garder billing.company
                company: formik.values.shippingAddressId === null 
                    ? shippingValues.company || "" 
                    : currentBillingCompany || shippingValues.company || ""
            };

            Object.keys(billingValues).forEach(key => {
                formik.setFieldValue(`billing.${key}`, billingValues[key]);
            });
        }

        if (!useSameAddress && user && user.addresses?.length > 0 && useSameAddressChanged) {
            // Seulement réinitialiser si billing n'a pas déjà une adresse sélectionnée
            if (!formik.values.billingAddressId) {
                formik.setFieldValue("billingAddressId", user.addresses[0].id);
                updateFormAddress('billing', user.addresses[0].id);
            }
        }

        // Mettre à jour les références
        previousUseSameAddress.current = useSameAddress;
        previousShippingAddressId.current = formik.values.shippingAddressId;
    }, [useSameAddress, formik.values.shippingAddressId, formik.values.shipping, user?.addresses])

    const filteredProducts = products?.filter(product =>
        product.type === activeTab && product.type !== 'capsule_spend'
    );



    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl font-bold mb-8 text-center">Nos Produits</h1>
                <div className="flex gap-8">
                    <div className="flex-grow">
                        {/* Tabs skeleton */}
                        <div className="flex gap-2 mb-8">
                            <div className="h-12 w-48 bg-base-300 rounded-lg animate-pulse"></div>
                            <div className="h-12 w-48 bg-base-300 rounded-lg animate-pulse"></div>
                        </div>

                        {/* Products Grid Skeleton */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <SkeletonCard count={6} />
                        </div>
                    </div>

                    {/* Cart skeleton */}
                    <div className="w-96 hidden lg:block">
                        <div className="card bg-base-100 shadow-lg">
                            <div className="card-header bg-primary text-primary-content p-4">
                                <div className="h-6 bg-primary-content/20 rounded w-20 animate-pulse"></div>
                            </div>
                            <div className="card-body p-4">
                                <div className="text-center text-base-content/60">
                                    Chargement du panier...
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (error) {
        toast.error('Erreur lors du chargement des produits');
        return <div className="text-center text-red-500">Erreur lors du chargement des produits</div>;
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-8 text-center">Nos Produits</h1>
            {isSubmitting && <LoadingSpinner fullscreen text="Traitement de votre commande..." />}
            <div className="flex gap-8">
                <div className="flex-grow">
                    {/* Tabs */}
                    <div className="tabs tabs-boxed  bg-transparent justify-start p-2 mb-8 space-x-2">
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                className={`w-fit tab tab-lg transition-all duration-200 ${activeTab === tab.id ? 'tab-active bg-primary text-white' : 'bg-transparent border border-solid border-primary'}`}
                                onClick={() => handleTabChange(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Products Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredProducts?.map((product) => (
                            <div
                                key={product.id}
                                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                                onClick={() => handleShowDetails(product)}
                            >
                                <div className="aspect-w-16 aspect-h-9 relative">
                                    <img
                                        src={product.imageUrl || '/assets/images/default-product-image.svg'}
                                        alt={product.name}
                                        className="w-full h-48 object-cover"
                                    />
                                    {product.salePrice && (
                                        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-sm">
                                            Promo
                                        </div>
                                    )}
                                </div>
                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <h2 className="text-xl font-semibold text-gray-800 line-clamp-2">{product.name}</h2>
                                        <InformationCircleIcon className="h-6 w-6 text-primary flex-shrink-0 hover:text-primary-focus" />
                                    </div>
                                    <div className="flex justify-between items-center mt-4">
                                        <div className="flex flex-col">
                                            {product.salePrice ? (
                                                <>
                                                    <span className="text-2xl font-bold text-primary">{product.salePrice} €</span>
                                                    <span className="text-sm text-gray-500 line-through">{product.regularPrice} €</span>
                                                </>
                                            ) : (
                                                <span className="text-2xl font-bold text-primary">{product.price} €</span>
                                            )}
                                        </div>
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={(e) => handleAddToCart(e, product)}
                                        >
                                            Ajouter au panier
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredProducts?.length === 0 && (
                        <div className="text-center text-gray-500 py-8">
                            Aucun produit disponible dans cette catégorie
                        </div>
                    )}
                </div>

                <div className="w-96 hidden lg:block sticky top-4 self-start">
                    <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                        <div className="p-4 bg-primary text-white">
                            <h2 className="text-xl font-bold">Panier</h2>
                        </div>
                        <Cart handleOrder={handleOrder} />
                    </div>
                </div>
            </div>
            <p className='text-error font-bold text-center mt-12 max-w-2xl mx-auto'>Pour un juste calcul des frais de ports lors d'envois de plusieurs capsules en même temps ou hors de France, nous vous recommandons de faire le  règlement via votre lien d'affiliation.</p>

            <PortalModal
                ref={modalRef}
                size="lg"
                className="max-h-[90vh] overflow-y-auto"
                onClose={() => {
                    // Réinitialiser tous les états pour permettre une nouvelle commande
                    setShowConfirmation(false);
                    setSelectedProduct(null);
                    setShowSuccessModal(false);
                    setShowAddressModal(false);
                    setIsCreatingAddresses(false);

                    // Réinitialiser les préférences utilisateur
                    setUseSameAddress(true);
                }}>
                {selectedProduct && <ProductDetails product={selectedProduct} />}
                {showConfirmation && (
                    <div className="p-4 bg-white rounded-lg shadow-lg">
                        <div className="flex items-center justify-center mb-4">
                            <ExclamationTriangleIcon className="h-20 w-20 text-yellow-500 mr-2" />
                            <p className="text-center font-semibold text-yellow-700">
                                <strong>Êtes-vous sûr de vouloir afficher les autres produits, ceci va réinitialiser le panier.</strong>
                            </p>
                        </div>
                        <div className="bg-yellow-100 p-4 space-y-2 rounded-lg mb-4">
                            <p className="text-center text-gray-700">
                                Vous ne pouvez pas ajouter d'autres produits quand vous commandez des capsules.
                            </p>
                        </div>
                        <div className="flex justify-center gap-4">
                            <button className="btn btn-warning btn-outline" onClick={() => confirmTabChange(true)}>Oui</button>
                            <button className="btn btn-primary" onClick={() => confirmTabChange(false)}>Non</button>
                        </div>
                    </div>
                )}
                {
                    showAddressModal && (
                        <div className="relative">
                            <ModalOverlay
                                isVisible={isSubmitting || isCreatingAddresses}
                                text={isCreatingAddresses ? "Création des adresses..." : "Traitement de votre commande..."}
                                size="lg"
                            />
                            {isCreatingAddresses && (
                                <div className="alert alert-info mb-4">
                                    <span>Création des adresses en cours...</span>
                                </div>
                            )}
                            <p className="text-3xl font-bold">
                                Choisissez votre adresse
                            </p>
                            <FormikProvider value={formik}>
                                <Form className='flex flex-col'>

                                    <AddressForm
                                        title={"Addresse de livraison"}
                                        addresses={user.addresses}
                                        type="shipping"
                                        disabled={isSubmitting}
                                        therapist={user}
                                        errors={formik.errors}
                                        showSectionTitle={false}
                                        showCompanyField={useSameAddress && !formik.values.shippingAddressId}
                                    />
                                    <div className="form-control w-fit my-4">
                                        <label className="label cursor-pointer space-x-4">
                                            <input id='use-same-addresses' type="checkbox" className="toggle toggle-primary  border-solid border-2" checked={useSameAddress} onChange={handleUseSameAddress} />
                                            <span className="label-text">Utiliser la même adresse pour la facturation</span>
                                        </label>
                                    </div>
                                    {!useSameAddress && (
                                        <AddressForm
                                            title={"Addresse de facturation"}
                                            addresses={user.addresses}
                                            type="billing"
                                            disabled={isSubmitting}
                                            therapist={user}
                                            errors={formik.errors}
                                            showSectionTitle={false}
                                        />
                                    )}

                                    <div className="flex justify-center gap-2 mt-6">
                                        <button
                                            type="submit"
                                            className="btn btn-lg btn-primary"
                                            disabled={isSubmitting || isCreatingAddresses}
                                        >
                                            {(isSubmitting || isCreatingAddresses) ? (
                                                <span className="flex items-center gap-2">
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    {isCreatingAddresses ? 'Création...' : 'Traitement...'}
                                                </span>
                                            ) : (
                                                'Valider ma commande'
                                            )}
                                        </button>
                                    </div>
                                </Form>
                            </FormikProvider>
                        </div>
                    )
                }
                {showSuccessModal && (
                    <SuccessMessage handlePayment={handlePayment} handleGoToOrders={handleGoToOrders} />
                )}
            </PortalModal>


        </div>
    );
};

export default Products;
